import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, Download, Heart, Share2, MessageCircle, Smile } from 'lucide-react';
import { mediaUrl, thumbUrl, api } from '@/lib/api';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const REACTIONS = ['❤️', '😍', '🔥', '👏', '🤩', '😂'];

export const MediaLightbox = ({ items = [], indexInitial = 0, open, onClose, onUpdated, sharePrefix, participants = [], readOnly = false }) => {
  const [index, setIndex] = useState(indexInitial);
  const [showReactions, setShowReactions] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [text, setText] = useState('');
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionMatches, setMentionMatches] = useState([]);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  // touch swipe state
  const touchRef = useRef({ x: 0, y: 0, t: 0 });

  // zoom/pan
  const [scale, setScale] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const pinchRef = useRef({ dist: 0, scale: 1 });

  const m = items[index];

  useEffect(() => { setIndex(indexInitial); }, [indexInitial, open]);
  useEffect(() => {
    setScale(1); setPos({ x: 0, y: 0 }); setShowComments(false); setShowReactions(false);
    setComments([]); setText('');
    if (open && m && !readOnly) {
      api.get(`/media/${m.id}/comments`).then((r) => setComments(r.data || [])).catch(() => {});
    }
  }, [m?.id, open, readOnly]);

  const next = useCallback(() => setIndex((i) => Math.min(i + 1, items.length - 1)), [items.length]);
  const prev = useCallback(() => setIndex((i) => Math.max(i - 1, 0)), []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
      else if (e.key === 'ArrowRight') next();
      else if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [open, onClose, next, prev]);

  const handleTouchStart = (e) => {
    if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      pinchRef.current.dist = Math.hypot(dx, dy);
      pinchRef.current.scale = scale;
      return;
    }
    touchRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, t: Date.now() };
  };
  const handleTouchMove = (e) => {
    if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const d = Math.hypot(dx, dy);
      if (pinchRef.current.dist > 0) {
        const ns = Math.max(1, Math.min(4, pinchRef.current.scale * (d / pinchRef.current.dist)));
        setScale(ns);
      }
      e.preventDefault();
    }
  };
  const handleTouchEnd = (e) => {
    if (scale > 1) return; // disable swipe while zoomed
    const t = touchRef.current;
    if (!t || !e.changedTouches?.[0]) return;
    const dx = e.changedTouches[0].clientX - t.x;
    const dy = e.changedTouches[0].clientY - t.y;
    if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      if (dx < 0) next(); else prev();
    } else if (dy > 80) {
      onClose?.();
    }
  };
  const handleDoubleClick = () => {
    setScale((s) => (s > 1 ? 1 : 2));
    setPos({ x: 0, y: 0 });
  };

  const downloadCurrent = async () => {
    try {
      const url = sharePrefix
        ? `${sharePrefix}/media/${m.id}/file`
        : mediaUrl(m.id);
      const a = document.createElement('a');
      a.href = url;
      a.download = m.original_filename || m.id;
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) { toast.error('Download failed'); }
  };

  const toggleFavorite = async () => {
    if (!m || readOnly) return;
    try {
      if (m.favorited) await api.delete(`/media/${m.id}/favorite`); else await api.post(`/media/${m.id}/favorite`);
      const updated = { ...m, favorited: !m.favorited };
      onUpdated?.(updated, index);
    } catch (e) { toast.error('Failed'); }
  };

  const sendReaction = async (emoji) => {
    if (!m || readOnly) return;
    try {
      await api.post(`/media/${m.id}/reactions`, { emoji });
      const reactions = { ...(m.reactions || {}) };
      const prevEmo = m.my_reaction;
      if (prevEmo === emoji) {
        reactions[emoji] = Math.max(0, (reactions[emoji] || 0) - 1);
        if (!reactions[emoji]) delete reactions[emoji];
        onUpdated?.({ ...m, reactions, my_reaction: null }, index);
      } else {
        if (prevEmo) {
          reactions[prevEmo] = Math.max(0, (reactions[prevEmo] || 0) - 1);
          if (!reactions[prevEmo]) delete reactions[prevEmo];
        }
        reactions[emoji] = (reactions[emoji] || 0) + 1;
        onUpdated?.({ ...m, reactions, my_reaction: emoji }, index);
      }
      setShowReactions(false);
    } catch (e) { toast.error('Failed'); }
  };

  const onChangeText = (val) => {
    setText(val);
    const at = val.lastIndexOf('@');
    if (at >= 0 && (at === 0 || /\s/.test(val[at - 1] || ' '))) {
      const frag = val.slice(at + 1).toLowerCase();
      if (!/\s/.test(frag)) {
        const matches = (participants || []).filter((p) => {
          const u = p.user || {};
          const handle = (u.email || '').split('@')[0];
          return handle.toLowerCase().includes(frag) || (u.name || '').toLowerCase().includes(frag);
        }).slice(0, 6);
        setMentionMatches(matches);
        setMentionOpen(true);
        return;
      }
    }
    setMentionOpen(false);
  };
  const insertMention = (u) => {
    const handle = (u.email || '').split('@')[0];
    const at = text.lastIndexOf('@');
    if (at < 0) return;
    const before = text.slice(0, at);
    setText(`${before}@${handle} `);
    setMentionOpen(false);
    inputRef.current?.focus();
  };

  const submitComment = async () => {
    const t = text.trim();
    if (!t || readOnly || busy) return;
    setBusy(true);
    try {
      const r = await api.post(`/media/${m.id}/comments`, { text: t });
      setComments((arr) => [...arr, r.data]);
      setText('');
    } catch (e) { toast.error('Failed'); }
    finally { setBusy(false); }
  };

  if (!open || !m) return null;

  const srcLarge = sharePrefix ? `${sharePrefix}/media/${m.id}/file` : mediaUrl(m.id);
  const srcThumb = sharePrefix ? `${sharePrefix}/media/${m.id}/file?thumb=true` : thumbUrl(m.id);

  return createPortal(
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[10001] bg-black/95 text-white" data-testid="media-lightbox">
        {/* Top bar */}
        <div className="absolute top-0 inset-x-0 z-10 flex items-center justify-between px-3 py-3 safe-top bg-gradient-to-b from-black/70 to-transparent">
          <div className="flex items-center gap-2 min-w-0">
            {m.uploader && (
              <>
                <Avatar className="h-8 w-8"><AvatarImage src={m.uploader.picture} /><AvatarFallback>{(m.uploader.name || 'U').slice(0,1)}</AvatarFallback></Avatar>
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate" data-testid="lightbox-uploader">{m.uploader.name || 'Member'}</div>
                  <div className="text-[11px] opacity-80 truncate">{m.taken_at ? new Date(m.taken_at).toLocaleString() : new Date(m.created_at).toLocaleString()}</div>
                </div>
              </>
            )}
          </div>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" onClick={downloadCurrent} data-testid="lightbox-download" className="text-white hover:bg-white/15 rounded-xl"><Download className="h-5 w-5" /></Button>
            <Button variant="ghost" size="icon" onClick={onClose} data-testid="lightbox-close" className="text-white hover:bg-white/15 rounded-xl"><X className="h-5 w-5" /></Button>
          </div>
        </div>

        {/* Media */}
        <div ref={containerRef} onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd} onDoubleClick={handleDoubleClick} className="absolute inset-0 flex items-center justify-center select-none" style={{ touchAction: 'pinch-zoom' }}>
          {m.is_video ? (
            <video key={m.id} controls autoPlay playsInline src={srcLarge} className="max-h-[100dvh] max-w-full" data-testid="lightbox-video" />
          ) : (
            <img
              key={m.id}
              src={srcLarge}
              alt={m.caption || ''}
              draggable={false}
              data-testid="lightbox-image"
              className="max-h-[100dvh] max-w-full transition-transform duration-200"
              style={{ transform: `scale(${scale}) translate(${pos.x}px, ${pos.y}px)` }}
            />
          )}
        </div>

        {/* Navigation arrows (desktop) */}
        {index > 0 && (
          <button onClick={prev} data-testid="lightbox-prev" className="hidden sm:flex absolute left-3 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-black/40 hover:bg-black/60 items-center justify-center"><ChevronLeft className="h-6 w-6" /></button>
        )}
        {index < items.length - 1 && (
          <button onClick={next} data-testid="lightbox-next" className="hidden sm:flex absolute right-3 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-black/40 hover:bg-black/60 items-center justify-center"><ChevronRight className="h-6 w-6" /></button>
        )}

        {/* Bottom action bar */}
        <div className="absolute bottom-0 inset-x-0 z-10 bg-gradient-to-t from-black/80 to-transparent">
          {/* Caption */}
          {m.caption && (
            <div className="px-4 pt-3 pb-2">
              <p className="text-sm leading-snug" data-testid="lightbox-caption">{m.caption}</p>
            </div>
          )}
          {/* Reactions summary */}
          {m.reactions && Object.keys(m.reactions).length > 0 && (
            <div className="px-4 flex items-center gap-2 flex-wrap pb-2">
              {Object.entries(m.reactions).map(([emo, c]) => (
                <button key={emo} onClick={() => sendReaction(emo)} data-testid={`reaction-chip-${emo}`} className={`px-2 py-1 rounded-full text-sm flex items-center gap-1 border ${m.my_reaction === emo ? 'bg-white text-black border-white' : 'bg-white/10 border-white/20 hover:bg-white/20'}`}>
                  <span>{emo}</span><span className="text-xs font-semibold">{c}</span>
                </button>
              ))}
            </div>
          )}
          {/* Actions */}
          {!readOnly && (
            <div className="px-3 py-3 safe-bottom flex items-center gap-2">
              <Button variant="ghost" size="icon" onClick={() => setShowReactions((v) => !v)} data-testid="lightbox-react-button" className="text-white hover:bg-white/15 rounded-xl"><Smile className="h-5 w-5" /></Button>
              <Button variant="ghost" size="icon" onClick={() => setShowComments(true)} data-testid="lightbox-comments-button" className="text-white hover:bg-white/15 rounded-xl relative">
                <MessageCircle className="h-5 w-5" />
                {comments.length > 0 && <span className="absolute -top-0.5 -right-0.5 bg-[hsl(var(--primary))] text-white text-[10px] rounded-full h-4 min-w-4 px-1 flex items-center justify-center">{comments.length}</span>}
              </Button>
              <Button variant="ghost" size="icon" onClick={toggleFavorite} data-testid="lightbox-favorite-button" className="text-white hover:bg-white/15 rounded-xl"><Heart className={`h-5 w-5 ${m.favorited ? 'fill-rose-500 text-rose-500' : ''}`} /></Button>
              <div className="ml-auto text-xs opacity-80" data-testid="lightbox-counter">{index + 1} / {items.length}</div>
            </div>
          )}
        </div>

        {/* Reactions popup */}
        {showReactions && (
          <div className="absolute bottom-20 left-3 right-3 sm:left-1/2 sm:-translate-x-1/2 sm:right-auto sm:w-auto bg-white text-black rounded-full shadow-xl py-1.5 px-2 flex items-center justify-around gap-1 z-20" data-testid="reaction-picker">
            {REACTIONS.map((e) => (
              <button key={e} onClick={() => sendReaction(e)} data-testid={`react-${e}`} className="h-10 w-10 rounded-full hover:bg-secondary text-2xl flex items-center justify-center transition-transform hover:scale-125">{e}</button>
            ))}
          </div>
        )}

        {/* Comments side drawer */}
        {showComments && !readOnly && (
          <motion.div initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }} transition={{ duration: 0.25 }} className="absolute right-0 top-0 bottom-0 w-full sm:max-w-md bg-card text-foreground z-20 flex flex-col" data-testid="comments-drawer">
            <div className="flex items-center justify-between p-3 border-b">
              <div className="font-serif text-lg font-semibold">Comments</div>
              <Button variant="ghost" size="icon" onClick={() => setShowComments(false)} className="rounded-xl"><X className="h-5 w-5" /></Button>
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-3" data-testid="comments-list">
              {comments.length === 0 && <div className="text-sm text-muted-foreground text-center py-8">Be the first to comment.</div>}
              {comments.map((c) => (
                <div key={c.id} className="flex gap-2" data-testid={`comment-${c.id}`}>
                  <Avatar className="h-8 w-8"><AvatarImage src={c.user?.picture} /><AvatarFallback>{(c.user?.name || 'U').slice(0,1)}</AvatarFallback></Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] text-muted-foreground">{c.user?.name} · {new Date(c.created_at).toLocaleString()}</div>
                    <div className="text-sm whitespace-pre-line break-words">{renderTextWithMentions(c.text)}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="border-t p-2 relative">
              {mentionOpen && mentionMatches.length > 0 && (
                <div data-testid="mention-suggestions" className="absolute bottom-full left-2 right-2 mb-1 bg-card border rounded-xl shadow-lg max-h-48 overflow-y-auto">
                  {mentionMatches.map((p) => (
                    <button key={p.id} onClick={() => insertMention(p.user || { email: p.invited_email })} data-testid={`mention-suggest-${(p.user?.email || p.invited_email)}`} className="w-full text-left px-3 py-2 hover:bg-secondary text-sm flex items-center gap-2">
                      <Avatar className="h-6 w-6"><AvatarImage src={p.user?.picture} /><AvatarFallback>{(p.user?.name || p.invited_email || 'U').slice(0,1)}</AvatarFallback></Avatar>
                      <span className="font-medium">{p.user?.name || p.invited_email}</span>
                      <span className="text-xs text-muted-foreground">@{(p.user?.email || p.invited_email || '').split('@')[0]}</span>
                    </button>
                  ))}
                </div>
              )}
              <div className="flex items-center gap-2">
                <input
                  ref={inputRef}
                  data-testid="comment-input"
                  className="flex-1 h-10 rounded-xl bg-secondary px-3 text-sm border border-input outline-none focus:ring-2 focus:ring-ring"
                  placeholder="Write a comment… use @ to mention"
                  value={text}
                  onChange={(e) => onChangeText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submitComment(); } }}
                />
                <Button onClick={submitComment} disabled={busy} data-testid="comment-submit" className="rounded-xl gradient-fab text-white">Send</Button>
              </div>
            </div>
          </motion.div>
        )}
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

function renderTextWithMentions(text) {
  if (!text) return null;
  const parts = text.split(/(@[\w._-]+)/g);
  return parts.map((p, i) => {
    if (p.startsWith('@')) {
      return (
        <span key={`mention-${i}-${p}`} className="text-[hsl(var(--primary))] font-medium">{p}</span>
      );
    }
    return <span key={`txt-${i}-${p.length}`}>{p}</span>;
  });
}
